import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import LoadingSpinner from '../components/LoadingSpinner';
import MediaRenderer from '../components/MediaRenderer';
import { apiService } from '../services/api';

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // Hero slider images - Muhteşem Türk Müzik Enstrümanları
  const heroSlides = [
    {
      image: '/images/Saz-Cesitleri-Nelerdir-Bilgi-Tarihce.webp',

      title: '🎸 Geleneksel Türk Bağlaması',
      subtitle: 'Anadolu\'nun bin yıllık müzik geleneğini yaşatan, usta ellerinden çıkan özel bağlamalar'
    },
    {
      image: '/images/pexels-photo-3120109.jpeg',
      title: '🎻 Profesyonel Telli Çalgılar',
      subtitle: 'Klasik müziğin vazgeçilmez enstrümanları, Avrupa kalitesinde Türk işçiliği'
    },
    {
      image: '/images/wp3145662.jpg',
      title: '🎼 Klasik & Flamenko Gitar',
      subtitle: 'Doğal ahşap malzemelerden, geleneksel yöntemlerle üretilen kaliteli gitarlar'
    },
    {
      image: '/images/istockphoto-2194178707-640x640.jpg',

      title: '🎺 Türk Halk Müziği Enstrümanları',
      subtitle: 'Ney, zurna, kaval gibi geleneksel nefesli çalgılar - Kültürel mirasımızın sesi'
    }
  ];


  useEffect(() => {
    const fetchData = async (retryCount = 0) => {
      try {
          setLoading(true);
          setError(null); // Hata durumunu sıfırla
          
          // Öne çıkan ürünleri getir
          const [productsResponse, categoriesResponse] = await Promise.all([
            apiService.getFeaturedProducts({ limit: 6 }),
            apiService.getMainCategories({ limit: 4 }).catch(err => {
              console.warn('Categories failed:', err.message);
              return { data: { main_categories: [] } };
            })
          ]);

        // API yanıtlarından veriyi çıkar
        const products = productsResponse?.data?.products || [];
        const rawCategories = categoriesResponse?.data?.main_categories || [];
        
        // Duplicate kategorileri temizle (id'ye göre)
        const uniqueCategories = rawCategories.filter((category, index, self) => 
          index === self.findIndex(c => c.id === category.id)
        );
        
        // Debug: Categories loaded
        console.log('✅ Categories loaded:', uniqueCategories.length, 'unique from', rawCategories.length, 'total');
        

        
        // Öne çıkan ürünleri ayarla
        setFeaturedProducts(products);
        setCategories(uniqueCategories);
              } catch (err) {
          console.error('❌ Veri yükleme hatası:', err);
          console.error('❌ Error details:', err.response || err.message);
          
          // Daha kullanıcı dostu hata mesajları
          let errorMessage = 'Veriler yüklenirken bir hata oluştu.';
          
          if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
            errorMessage = 'Bağlantı zaman aşımına uğradı. Lütfen tekrar deneyin.';
          } else if (err.response?.status === 500) {
            errorMessage = 'Sunucu hatası oluştu. Lütfen daha sonra tekrar deneyin.';
          } else if (err.response?.status === 404) {
            errorMessage = 'Veri bulunamadı.';
          } else if (!navigator.onLine) {
            errorMessage = 'İnternet bağlantınızı kontrol edin.';
          }
          
          setError(errorMessage);
          
          // Retry logic - maksimum 2 kez dene
          if (retryCount < 2 && (err.code === 'ECONNABORTED' || err.response?.status >= 500)) {
            console.log(`🔄 Retry attempt ${retryCount + 1}/2`);
            setTimeout(() => fetchData(retryCount + 1), 2000 * (retryCount + 1)); // Exponential backoff
            return;
          }
          
          // Fallback data - en azından boş state göster
          setFeaturedProducts([]);
          setCategories([]);
        } finally {
          setLoading(false);
        }
    };

    fetchData();
  }, []);

  // Hero slider auto-play
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [heroSlides.length]);

  // Scroll animations - Her scroll'da çalışır
  useEffect(() => {
    const observerOptions = {
      threshold: 0.05, // Daha düşük threshold - daha erken tetiklenir
      rootMargin: '0px 0px -20px 0px' // Daha az margin - daha hassas
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // Element görünür olduğunda animasyonu başlat
          entry.target.classList.add('animate-visible');
        } else {
          // Element görünmez olduğunda animasyonu sıfırla
          entry.target.classList.remove('animate-visible');
        }
      });
    }, observerOptions);

    // Animate edilecek elementleri seç
    const animateElements = document.querySelectorAll('.animate-on-scroll');
    animateElements.forEach((el) => observer.observe(el));

    return () => {
      animateElements.forEach((el) => observer.unobserve(el));
    };
  }, [categories, featuredProducts]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger text-center">
          <h4>Hata</h4>
          <p>{error}</p>
          <button 
            className="btn btn-primary" 
            onClick={() => window.location.reload()}
          >
            Tekrar Dene
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Hero Section with Slider */}
      <section className="hero-slider-section">
        {heroSlides.map((slide, index) => (
          <div
            key={index}
            className={`hero-slide ${index === currentSlide ? 'active' : ''}`}
            style={{ backgroundImage: `url(${slide.image})` }}
            role="img"
            aria-label={`Işıltı Saz Evi ${slide.title} - Geleneksel Türk Müzik Enstrümanları`}
          >
            <div className="hero-overlay">
              <div className="container">
                <div className="row align-items-center h-100">
                  <div className="col-lg-8 mx-auto text-center text-white">
                    <h1 className="display-4 fw-bold mb-4">
                      {slide.title}
                    </h1>
                    <p className="lead mb-5">
                      {slide.subtitle}
                    </p>
                    <div className="d-flex gap-3 justify-content-center flex-wrap">
                      <Link to="/products" className="btn btn-primary btn-lg">
                        Ürünleri İncele
                      </Link>
                      <Link to="/categories" className="btn btn-outline-light btn-lg">
                        Kategoriler
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
        
        {/* Navigation dots */}
        <div className="hero-dots">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              className={`hero-dot ${index === currentSlide ? 'active' : ''}`}
              onClick={() => setCurrentSlide(index)}
              aria-label={`Slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Sabit Butonlar */}
        <Link to="/products" className="btn btn-primary btn-lg">
          Ürünleri İncele
        </Link>
        <Link to="/categories" className="btn btn-outline-light btn-lg">
          Kategoriler
        </Link>
      </section>

      {/* Öne Çıkan Ürünler */}
      <section className="section py-5 products-section" style={{backgroundColor: 'var(--mint-light)', backgroundImage: 'linear-gradient(135deg, var(--mint-light) 0%, #ffffff 100%)'}}>
        <div className="container">
          <div className="row mb-5">
            <div className="col-12 text-center animate-on-scroll">
              <h2 className="display-5 fw-bold mb-4 section-title">
                {featuredProducts.length > 0 ? 'Öne Çıkan Ürünler' : 'En Yeni Ürünler'}
              </h2>
              <p className="text-muted">
                Müzik severlerin favorisi olan en kaliteli ürünlerimiz
              </p>
            </div>
          </div>
          
          {featuredProducts.length > 0 ? (
            <div className="row">
              {featuredProducts.map((product, index) => (
                <div 
                  key={product.id} 
                  className="col-lg-4 col-md-6 mb-4 animate-on-scroll"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <ProductCard product={product} />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-5">
              <i className="bi bi-music-note-list display-1 text-muted mb-3"></i>
              <p className="text-muted">Henüz ürün bulunmuyor.</p>
            </div>
          )}
          
          {featuredProducts.length > 0 && (
            <div className="text-center mt-5">
              <Link to="/products" className="btn btn-primary btn-lg">
                <i className="bi bi-arrow-right me-2"></i>
                Tüm Ürünleri Gör
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Kargo & Teslimat */}
      <section className="simple-shipping-section py-5" style={{backgroundColor: 'var(--peach-light)', backgroundImage: 'linear-gradient(135deg, var(--peach-light) 0%, #ffffff 100%)'}}>
        <div className="container">
          <div className="row mb-5">
            <div className="col-lg-8 mx-auto text-center animate-on-scroll">
              <h2 className="display-6 fw-bold mb-3">Güvenli Teslimat</h2>
              <p className="lead text-muted">
                Müzik enstrümanlarınızı dünyanın her yerine güvenle ulaştırıyoruz
              </p>
            </div>
          </div>

          <div className="row align-items-center mb-5">
            <div className="col-lg-6 animate-on-scroll">
              <div className="shipping-features">
                <div className="feature-item mb-4">
                  <div className="feature-icon">
                    <i className="bi bi-shield-check"></i>
                  </div>
                  <div className="feature-content">
                    <h5>Kırılmaz Ambalaj</h5>
                    <p className="text-muted">Özel koruyucu malzemelerle güvenli paketleme</p>
                  </div>
                </div>
                
                <div className="feature-item mb-4">
                  <div className="feature-icon">
                    <i className="bi bi-truck"></i>
                  </div>
                  <div className="feature-content">
                    <h5>Hızlı Kargo</h5>
                    <p className="text-muted">Dünya genelinde 3-7 iş günü teslimat</p>
                  </div>
                </div>
                
                <div className="feature-item">
                  <div className="feature-icon">
                    <i className="bi bi-geo-alt"></i>
                  </div>
                  <div className="feature-content">
                    <h5>Global Teslimat</h5>
                    <p className="text-muted">200+ ülkeye güvenli gönderim</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-6 animate-on-scroll">
              <div className="shipping-stats">
                <div className="row text-center">
                  <div className="col-6 mb-4">
                    <div className="stat-item">
                      <div className="stat-number">200+</div>
                      <div className="stat-label">Ülke</div>
                    </div>
                  </div>
                  <div className="col-6 mb-4">
                    <div className="stat-item">
                      <div className="stat-number">99.8%</div>
                      <div className="stat-label">Güvenlik</div>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="stat-item">
                      <div className="stat-number">3-7</div>
                      <div className="stat-label">İş Günü</div>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="stat-item">
                      <div className="stat-number">50K+</div>
                      <div className="stat-label">Müşteri</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-12 animate-on-scroll">
              <div className="shipping-guarantee text-center">
                <div className="guarantee-badge">
                  <i className="bi bi-patch-check me-2"></i>
                  %100 Güvenli Teslimat Garantisi
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Kategoriler */}
      <section className="section py-5" style={{backgroundColor: 'var(--ice-blue)', backgroundImage: 'linear-gradient(135deg, var(--ice-blue) 0%, #ffffff 100%)'}}>
        <div className="container">
          <div className="row mb-5">
            <div className="col-12 text-center animate-on-scroll">
              <h2 className="display-5 fw-bold mb-4 section-title">
                Kategoriler
              </h2>
              <p className="text-muted">
                Geniş ürün yelpazemizden size uygun olanı seçin
              </p>
            </div>
          </div>
          
          {categories.length > 0 ? (
            <div className="row">
              {categories.map((category, index) => (
                <div 
                  key={category.id} 
                  className="col-lg-3 col-md-6 mb-4 animate-on-scroll"
                  style={{ animationDelay: `${index * 0.15}s` }}
                >
                  <Link 
                    to={`/products?category=${category.id}`}
                    className="text-decoration-none"
                  >
                    <div className="card h-100 text-center p-4">
                      <div className="category-image-container">
                        <div className="category-image-wrapper">
                          <MediaRenderer
                            src={category.image || category.image_url || category.main_image}
                            alt={`Işıltı Saz Evi ${category.name} - Geleneksel Türk Müzik Enstrümanları`}
                            className="category-image"
                            loading="lazy"
                            controls={false}
                            autoPlay={false}
                            muted={true}
                          />
                        </div>
                      </div>
                      <h5 className="fw-bold mb-3" style={{color: '#111827'}}>
                        {category.name}
                      </h5>
                      <p className="text-muted small mb-0">
                        {category.description || `${category.name} kategorisindeki özel ürünlerimizi keşfedin.`}
                      </p>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-5">
              <div className="text-muted">
                <i className="bi bi-tags display-4 mb-3"></i>
                <p>Kategori bulunmuyor.</p>
              </div>
            </div>
          )}
          
          {categories.length > 0 && (
            <div className="text-center mt-5">
              <Link to="/categories" className="btn btn-outline-primary">
                Tüm Kategorileri Gör
                <i className="bi bi-arrow-right ms-2"></i>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Müşteri Yorumları */}
      <section className="section py-5" style={{backgroundColor: 'var(--warm-cream)', backgroundImage: 'radial-gradient(circle at center, rgba(255,255,255,0.8) 0%, transparent 70%)'}}>
        <div className="container">
          <div className="row mb-5">
            <div className="col-12 text-center animate-on-scroll">
              <h2 className="display-5 fw-bold mb-4 section-title">
                Müşteri Yorumları
              </h2>
              <p className="text-muted">
                Bizden alışveriş yapan müşterilerimizin deneyimleri
              </p>
            </div>
          </div>
          
          <div className="row">
            <div className="col-lg-4 col-md-6 mb-4 animate-on-scroll" style={{ animationDelay: '0.1s' }}>
              <div className="card h-100 p-4">
                <div className="mb-3">
                  <div className="d-flex mb-2">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="bi bi-star-fill text-warning me-1"></i>
                    ))}
                  </div>
                  <p className="mb-3" style={{fontStyle: 'italic', color: '#6b7280'}}>
                    "Satın aldığım bağlama gerçekten mükemmel! Hem ses kalitesi hem de işçilik açısından beklentilerimin üzerinde. Kesinlikle tavsiye ederim."
                  </p>
                </div>
                <div className="d-flex align-items-center">
                  <div 
                    className="rounded-circle d-flex align-items-center justify-content-center me-3"
                    style={{
                      width: '50px',
                      height: '50px',
                      backgroundColor: '#f3f4f6',
                      color: '#6b7280',
                      fontWeight: 'bold'
                    }}
                  >
                    AK
                  </div>
                  <div>
                    <h6 className="mb-0 fw-bold">Ahmet Kaya</h6>
                    <small className="text-muted">Müzik Öğretmeni</small>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="col-lg-4 col-md-6 mb-4 animate-on-scroll" style={{ animationDelay: '0.2s' }}>
              <div className="card h-100 p-4">
                <div className="mb-3">
                  <div className="d-flex mb-2">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="bi bi-star-fill text-warning me-1"></i>
                    ))}
                  </div>
                  <p className="mb-3" style={{fontStyle: 'italic', color: '#6b7280'}}>
                    "Oğlum için aldığım gitardan çok memnun kaldık. Kaliteli malzeme ve özenli işçilik. Müşteri hizmetleri de oldukça ilgili."
                  </p>
                </div>
                <div className="d-flex align-items-center">
                  <div 
                    className="rounded-circle d-flex align-items-center justify-content-center me-3"
                    style={{
                      width: '50px',
                      height: '50px',
                      backgroundColor: '#f3f4f6',
                      color: '#6b7280',
                      fontWeight: 'bold'
                    }}
                  >
                    FÖ
                  </div>
                  <div>
                    <h6 className="mb-0 fw-bold">Fatma Özkan</h6>
                    <small className="text-muted">Veli</small>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="col-lg-4 col-md-6 mb-4 animate-on-scroll" style={{ animationDelay: '0.3s' }}>
              <div className="card h-100 p-4">
                <div className="mb-3">
                  <div className="d-flex mb-2">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="bi bi-star-fill text-warning me-1"></i>
                    ))}
                  </div>
                  <p className="mb-3" style={{fontStyle: 'italic', color: '#6b7280'}}>
                    "20 yıldır müzikle uğraşıyorum ve bu kadar kaliteli el işçiliği görmemiştim. Profesyonel müzisyenlere kesinlikle öneriyorum."
                  </p>
                </div>
                <div className="d-flex align-items-center">
                  <div 
                    className="rounded-circle d-flex align-items-center justify-content-center me-3"
                    style={{
                      width: '50px',
                      height: '50px',
                      backgroundColor: '#f3f4f6',
                      color: '#6b7280',
                      fontWeight: 'bold'
                    }}
                  >
                    MG
                  </div>
                  <div>
                    <h6 className="mb-0 fw-bold">Mehmet Güneş</h6>
                    <small className="text-muted">Profesyonel Müzisyen</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Neden Biz - Enhanced */}
      <section className="features-section">
        <div className="container">
          <div className="row mb-5">
            <div className="col-lg-8 mx-auto text-center animate-on-scroll">
              <h2 className="display-5 fw-bold mb-4 section-title">
                Neden Işıltı Saz Evi?
              </h2>
              <p className="lead text-muted mb-0">
                15 yıllık deneyimimiz ve binlerce mutlu müşterimizle, Türkiye'nin en güvenilir 
                müzik aleti üreticilerinden biriyiz. İşte bizi özel kılan değerler.
              </p>
            </div>
          </div>
          
          <div className="row g-4">
            <div className="col-lg-6 col-md-6 mb-4 animate-on-scroll" style={{ animationDelay: '0.1s' }}>
              <div className="feature-card-enhanced">
                <div className="feature-icon-enhanced icon-blue">
                  <i className="bi bi-award"></i>
                </div>
                <div className="feature-content">
                  <h4 className="fw-bold mb-3">Kaliteli İşçilik</h4>
                  <p className="text-muted mb-3">
                    Deneyimli ustalarımız tarafından özenle üretilen her bir enstrüman, 
                    yıllarca kullanım için tasarlanmıştır. Geleneksel yöntemlerle modern teknoloji birleşir.
                  </p>
                  <div className="feature-stats">
                    <span className="stat-number">15+</span>
                    <span className="stat-label">Yıl Deneyim</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="col-lg-6 col-md-6 mb-4 animate-on-scroll" style={{ animationDelay: '0.2s' }}>
              <div className="feature-card-enhanced">
                <div className="feature-icon-enhanced icon-green">
                  <i className="bi bi-tree"></i>
                </div>
                <div className="feature-content">
                  <h4 className="fw-bold mb-3">Doğal Malzemeler</h4>
                  <p className="text-muted mb-3">
                    Sadece en kaliteli ve doğal ahşap malzemeler kullanarak, 
                    çevreye saygılı üretim yapıyoruz. Her ağaç özenle seçilir.
                  </p>
                  <div className="feature-stats">
                    <span className="stat-number">%100</span>
                    <span className="stat-label">Doğal Malzeme</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="col-lg-6 col-md-6 mb-4 animate-on-scroll" style={{ animationDelay: '0.3s' }}>
              <div className="feature-card-enhanced">
                <div className="feature-icon-enhanced icon-red">
                  <i className="bi bi-heart"></i>
                </div>
                <div className="feature-content">
                  <h4 className="fw-bold mb-3">Müşteri Memnuniyeti</h4>
                  <p className="text-muted mb-3">
                    Müşterilerimizin memnuniyeti bizim önceliğimizdir. 
                    Satış sonrası destek ve garanti hizmetlerimizle yanınızdayız.
                  </p>
                  <div className="feature-stats">
                    <span className="stat-number">1000+</span>
                    <span className="stat-label">Mutlu Müşteri</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="col-lg-6 col-md-6 mb-4 animate-on-scroll" style={{ animationDelay: '0.4s' }}>
              <div className="feature-card-enhanced">
                <div className="feature-icon-enhanced icon-orange">
                  <i className="bi bi-truck"></i>
                </div>
                <div className="feature-content">
                  <h4 className="fw-bold mb-3">Hızlı Teslimat</h4>
                  <p className="text-muted mb-3">
                    Türkiye'nin her yerine güvenli ambalajlama ile 
                    hızlı teslimat yapıyoruz. Özel kargo anlaşmalarımızla en uygun fiyat.
                  </p>
                  <div className="feature-stats">
                    <span className="stat-number">1-3</span>
                    <span className="stat-label">Gün Teslimat</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* CTA Section */}
          <div className="row mt-5">
            <div className="col-12 text-center animate-on-scroll" style={{ animationDelay: '0.5s' }}>
              <div className="cta-box">
                <h3 className="fw-bold mb-3">Hayalinizdeki Enstrümanı Bulun</h3>
                <p className="text-muted mb-4">
                  Geniş ürün yelpazemizden size en uygun müzik aletini seçin
                </p>
                <Link to="/products" className="btn btn-primary btn-lg me-3">
                  <i className="bi bi-grid me-2"></i>
                  Tüm Ürünleri İncele
                </Link>
                <Link to="/contact" className="btn btn-outline-primary btn-lg">
                  <i className="bi bi-telephone me-2"></i>
                  İletişime Geç
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Özel Yapım Sipariş */}
      <section className="section py-5" style={{backgroundColor: 'var(--burgundy)', background: 'linear-gradient(135deg, var(--burgundy) 0%, var(--rose-red) 100%)'}}>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-8 mb-4 mb-lg-0 animate-on-scroll">
              <div className="text-white">
                <h2 className="display-5 fw-bold mb-4">
                  <i className="bi bi-star-fill me-3"></i>
                  Özel Yapım Sipariş Alınır
                </h2>
                <p className="lead mb-4">
                  İstediğiniz ölçü, renk ve detaylarda özel saz ve müzik aletleri üretimi yapıyoruz. 
                  Usta ellerden çıkan, size özel tasarlanmış enstrümanlar.
                </p>
                <div className="row mb-4">
                  <div className="col-md-6 mb-3">
                    <div className="d-flex align-items-center text-white">
                      <div className="contact-icon me-3" style={{background: 'rgba(255,255,255,0.2)'}}>
                        <i className="bi bi-palette-fill"></i>
                      </div>
                      <div>
                        <h6 className="mb-1 fw-bold">Özel Tasarım</h6>
                        <p className="mb-0 opacity-75">Size özel renk ve desen</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <div className="d-flex align-items-center text-white">
                      <div className="contact-icon me-3" style={{background: 'rgba(255,255,255,0.2)'}}>
                        <i className="bi bi-rulers"></i>
                      </div>
                      <div>
                        <h6 className="mb-1 fw-bold">İstediğiniz Ölçü</h6>
                        <p className="mb-0 opacity-75">Kişiye özel boyutlandırma</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <div className="d-flex align-items-center text-white">
                      <div className="contact-icon me-3" style={{background: 'rgba(255,255,255,0.2)'}}>
                        <i className="bi bi-award-fill"></i>
                      </div>
                      <div>
                        <h6 className="mb-1 fw-bold">Kaliteli Malzeme</h6>
                        <p className="mb-0 opacity-75">Doğal ahşap ve malzemeler</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <div className="d-flex align-items-center text-white">
                      <div className="contact-icon me-3" style={{background: 'rgba(255,255,255,0.2)'}}>
                        <i className="bi bi-clock-fill"></i>
                      </div>
                      <div>
                        <h6 className="mb-1 fw-bold">Hızlı Teslimat</h6>
                        <p className="mb-0 opacity-75">15-30 gün arası teslim</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="d-flex gap-3 flex-wrap">
                  <Link to="/contact" className="btn btn-light btn-lg">
                    <i className="bi bi-envelope me-2"></i>
                    Özel Sipariş Ver
                  </Link>
                  <a 
                    href="https://wa.me/905356091300?text=Merhaba, özel yapım sipariş hakkında bilgi almak istiyorum."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-outline-light btn-lg"
                  >
                    <i className="bi bi-whatsapp me-2"></i>
                    WhatsApp
                  </a>
                </div>
              </div>
            </div>
            <div className="col-lg-4 animate-on-scroll">
              <div className="custom-order-visual">
                <div className="custom-order-badge">
                  <i className="bi bi-star-fill mb-2"></i>
                  <h5 className="fw-bold mb-2">Özel Yapım</h5>
                  <p className="mb-0 small">Size Özel Tasarım</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bize Ulaş */}
      <section className="section py-5 contact-section" style={{backgroundColor: 'var(--purple-light)', backgroundImage: 'linear-gradient(135deg, var(--purple-light) 0%, #ffffff 100%)'}}>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-4 mb-lg-0 animate-on-scroll">
              <h2 className="display-5 fw-bold mb-4">
                Bizimle İletişime Geçin
              </h2>
              <p className="lead text-muted mb-4">
                Ürünlerimiz hakkında detaylı bilgi almak, özel siparişleriniz için 
                bizimle iletişime geçebilirsiniz. Uzman ekibimiz size yardımcı olmaktan mutluluk duyar.
              </p>
              <div className="row mb-4">
                <div className="col-md-6 mb-3">
                  <div className="d-flex align-items-center">
                    <div className="contact-icon me-3">
                      <i className="bi bi-telephone-fill"></i>
                    </div>
                    <div>
                      <h6 className="mb-1 fw-bold">Telefon</h6>
                      <p className="mb-0 text-muted">+90 535 609 13 00</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <div className="d-flex align-items-center">
                    <div className="contact-icon me-3">
                      <i className="bi bi-envelope-fill"></i>
                    </div>
                    <div>
                      <h6 className="mb-1 fw-bold">E-posta</h6>
                      <p className="mb-0 text-muted">info@isiltisazevi.com</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <div className="d-flex align-items-center">
                    <div className="contact-icon me-3">
                      <i className="bi bi-geo-alt-fill"></i>
                    </div>
                    <div>
                      <h6 className="mb-1 fw-bold">Adres</h6>
                      <p className="mb-0 text-muted">Çevik Bir Meydanı, Vali Rahmi Bey, 127. Sk. No:18/A, 35380 Buca/İzmir</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <div className="d-flex align-items-center">
                    <div className="contact-icon me-3">
                      <i className="bi bi-clock-fill"></i>
                    </div>
                    <div>
                      <h6 className="mb-1 fw-bold">Çalışma Saatleri</h6>
                      <p className="mb-0 text-muted">09:00 - 18:00</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="d-flex gap-3 flex-wrap">
                <Link to="/contact" className="btn btn-primary btn-lg">
                  <i className="bi bi-envelope me-2"></i>
                  İletişim Sayfası
                </Link>
                <a 
                  href="https://wa.me/905356091300?text=Merhaba, ürünleriniz hakkında bilgi almak istiyorum."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-success btn-lg"
                >
                  <i className="bi bi-whatsapp me-2"></i>
                  WhatsApp
                </a>
              </div>
            </div>
            <div className="col-lg-6 animate-on-scroll">
              <div className="map-wrapper">
                <div className="map-container rounded shadow-lg overflow-hidden">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3127.4285136119115!2d27.168626200000002!3d38.38534!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14bbd8ab5b457f31%3A0xe110989625898322!2zScWeSUxUSSBTQVogRVbEsA!5e0!3m2!1str!2str!4v1756673624343!5m2!1str!2str"
                    width="100%"
                    height="400"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Işıltı Saz Evi Konum - İzmir"
                  ></iframe>
                </div>
                <div className="map-info-overlay">
                  <div className="bg-white p-3 rounded shadow-sm border-start border-primary border-4">
                    <div className="d-flex align-items-center">
                      <div className="contact-icon me-3 text-primary">
                        <i className="bi bi-geo-alt-fill"></i>
                      </div>
                      <div>
                        <h6 className="mb-1 fw-bold">Mağazamızı Ziyaret Edin</h6>
                        <p className="mb-0 text-muted small">Çevik Bir Meydanı, Buca/İzmir</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/905356091300?text=Merhaba, ürünleriniz hakkında bilgi almak istiyorum."
        target="_blank"
        rel="noopener noreferrer"
        className="whatsapp-float"
        aria-label="WhatsApp ile iletişime geç"
      >
        <i className="bi bi-whatsapp"></i>
      </a>
    </div>
  );
};

export default Home;
